import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Phone, MessageCircle, Mail, X } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDate, formatErr } from "../../lib/api";
import { Loading, PageHeader, StatusPill } from "../../components/Shared";
import { useAuth } from "../../context/AuthContext";

const STATUSES = ["new", "contacted", "assessment_scheduled", "converted", "closed"];

function LeadPanel({ lead, onClose, onSaved, canManage }) {
  const [form, setForm] = useState({ status: lead.status, assessment_at: lead.assessment_at || "", note: "" });
  const [busy, setBusy] = useState(false);
  const digits = (lead.phone || "").replace(/\D/g, "");
  const save = async () => {
    const payload = {};
    if (form.status !== lead.status) payload.status = form.status;
    if (form.assessment_at && form.assessment_at !== (lead.assessment_at || "")) payload.assessment_at = form.assessment_at;
    if (form.note.trim()) payload.note = form.note.trim();
    if (!Object.keys(payload).length) { toast.error("Nothing to update"); return; }
    setBusy(true);
    try { await api.patch(`/admin/leads/${lead.id}`, payload); toast.success("Lead updated"); onSaved(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-stone-900/50 sm:items-center sm:p-4" data-testid="lead-panel">
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-heading text-xl font-bold text-stone-900">{lead.name}</h2>
            <p className="text-sm capitalize text-stone-500">{lead.type.replace(/_/g, " ")} · {fmtDate(lead.created_at)}</p>
          </div>
          <button onClick={onClose} className="rounded-full border border-stone-200 p-2 text-stone-500" data-testid="lead-panel-close"><X className="h-4 w-4" /></button>
        </div>

        <div className="mt-4 flex flex-wrap gap-2" data-testid="lead-contact-actions">
          {lead.phone && <a href={`tel:${lead.phone}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-900 px-3.5 py-2 text-xs font-bold text-white" data-testid="lead-call"><Phone className="h-3.5 w-3.5" /> Call {lead.phone}</a>}
          {digits && <a href={`https://wa.me/${digits}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full border border-emerald-800 px-3.5 py-2 text-xs font-bold text-emerald-900" data-testid="lead-whatsapp"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>}
          {lead.email && <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1 rounded-full border border-stone-200 px-3.5 py-2 text-xs font-semibold text-stone-600" data-testid="lead-email"><Mail className="h-3.5 w-3.5" /> {lead.email}</a>}
        </div>

        {lead.message && <p className="mt-4 rounded-xl bg-stone-50 p-3 text-sm text-stone-700" data-testid="lead-message">{lead.message}</p>}

        {canManage && (
          <div className="mt-5 space-y-3" data-testid="lead-actions">
            <div>
              <label className="p1-label">Status</label>
              <select className="p1-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="lead-panel-status">
                {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
            </div>
            <div>
              <label className="p1-label">Assessment date & time</label>
              <input type="datetime-local" className="p1-input" value={form.assessment_at} onChange={(e) => setForm({ ...form, assessment_at: e.target.value })} data-testid="lead-assessment-at" />
              <p className="mt-1 text-xs text-stone-400">Setting a date auto-marks the lead as “assessment scheduled” while it is new/contacted; later statuses are unaffected.</p>
            </div>
            <div>
              <label className="p1-label">Add note</label>
              <textarea className="p1-input" rows={2} placeholder="Call outcome, next step…" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="lead-note" />
            </div>
            <button onClick={save} disabled={busy} className="w-full rounded-xl bg-emerald-900 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50" data-testid="lead-save">
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        )}

        {(lead.notes || []).length > 0 && (
          <div className="mt-5">
            <p className="p1-label">Notes</p>
            <ul className="mt-2 space-y-2" data-testid="lead-notes">
              {[...lead.notes].reverse().map((n, i) => (
                <li key={i} className="rounded-xl bg-stone-50 p-2.5 text-xs">
                  <span className="text-stone-400">{fmtDate(n.at)}{n.by ? ` · ${n.by}` : ""}</span>
                  <p className="mt-0.5 text-stone-700">{n.note}</p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Leads() {
  const { user } = useAuth();
  const canManage = ["ops_manager", "admin", "super_admin"].includes(user?.role);
  const [leads, setLeads] = useState(null);
  const [open, setOpen] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const load = () => api.get("/admin/leads").then((r) => setLeads(r.data));
  useEffect(() => { load().catch(() => setLeads([])); }, []);
  useEffect(() => {
    const oid = searchParams.get("open");
    if (oid && leads) {
      const l = leads.find((x) => x.id === oid);
      if (l) setOpen(l);
      setSearchParams({}, { replace: true });
    }
  }, [leads]);
  if (!leads) return <Loading />;
  return (
    <div data-testid="admin-leads">
      <PageHeader title="Leads" sub="Assessment bookings, contact requests, onboarding submissions and career applications. Tap a lead to work it." />
      <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead className="bg-stone-50 text-left text-xs uppercase tracking-wider text-stone-500">
            <tr><th className="p-3">Name</th><th className="p-3">Type</th><th className="p-3">Contact</th><th className="hidden p-3 md:table-cell">Message</th><th className="p-3">When</th><th className="p-3">Status</th></tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {leads.map((l) => (
              <tr key={l.id} onClick={() => setOpen(l)} className="cursor-pointer hover:bg-emerald-50/40" data-testid={`lead-row-${l.id}`}>
                <td className="p-3 font-medium text-stone-800">{l.name}</td>
                <td className="p-3 capitalize text-stone-500">{l.type.replace(/_/g, " ")}</td>
                <td className="p-3 text-stone-500">{l.phone}<br />{l.email}</td>
                <td className="hidden max-w-[240px] truncate p-3 text-stone-500 md:table-cell">{l.message}</td>
                <td className="p-3 text-xs text-stone-400">{fmtDate(l.created_at)}</td>
                <td className="p-3"><StatusPill status={l.status} /></td>
              </tr>
            ))}
            {leads.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-stone-400">No leads yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <LeadPanel lead={leads.find((x) => x.id === open.id) || open} canManage={canManage}
        onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}
    </div>
  );
}
